import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LinearProgress from '@mui/material/LinearProgress'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Radio from '@mui/material/Radio'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'

// Lets dispatch set, change, or remove the vehicle on a duty without leaving
// the control screen. The server (`/pts/assign-vehicle-to-duty`) runs the real
// availability-conflict check — the "In use" chips here are day-level hints.
const AssignVehicleToDutyDialog = ({ open, duty, onClose, onAssigned }) => {
  const isPhone = useIsMobile('sm')
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [selectedVehicleID, setSelectedVehicleID] = useState(null)

  const currentVehicleID = duty?.vehicle?._id || duty?.vehicle || null

  useEffect(() => {
    if (!open || !duty?.start) return
    setError(null)
    setSelectedVehicleID(currentVehicleID)
    setLoading(true)
    // Send the duty's own window so vehicles on other duties that day (but
    // not at this time) aren't flagged busy. date remains the fallback.
    const params = new URLSearchParams({
      date: moment(duty.start).format('YYYY-MM-DD'),
      start: moment(duty.start).toISOString(),
    })
    if (duty.end) params.set('end', moment(duty.end).toISOString())
    API.get(`/pts/journeys/get-vehicles?${params.toString()}`)
      .then((res) => {
        if (res.status === 200) {
          setVehicles(res.data || [])
        } else {
          setError('Unable to load vehicles. Please try again.')
        }
      })
      .catch(() => setError('Unable to load vehicles. Please try again.'))
      .finally(() => setLoading(false))
  }, [open, duty?.start, duty?.end, currentVehicleID])

  const save = async (vehicleID) => {
    if (!duty?._id) return
    setSaving(true)
    setError(null)
    try {
      const res = await API.post('/pts/assign-vehicle-to-duty', {
        dutyID: duty._id,
        vehicleID,
      })
      if (res.status === 200) {
        onAssigned?.()
        onClose?.()
      } else {
        setError(
          res.data?.vehicleConflict?.message ||
            res.data?.error ||
            'Unable to assign this vehicle.',
        )
      }
    } catch (e) {
      setError(
        e?.response?.data?.vehicleConflict?.message ||
          e?.response?.data?.error ||
          'Unable to assign this vehicle.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? null : onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={isPhone}
    >
      <DialogTitle sx={{ fontWeight: 900 }}>
        {currentVehicleID ? 'Change duty vehicle' : 'Assign a vehicle'}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading && <LinearProgress />}
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Duty {moment(duty?.start).format('Do MMM HH:mm')} –{' '}
            {moment(duty?.end).format(
              moment(duty?.start).isSame(duty?.end, 'day')
                ? 'HH:mm'
                : 'Do MMM HH:mm',
            )}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
        <List dense>
          {vehicles.map((vehicle) => {
            const isCurrent = String(vehicle._id) === String(currentVehicleID)
            return (
              <ListItemButton
                key={vehicle._id}
                selected={String(selectedVehicleID) === String(vehicle._id)}
                onClick={() => setSelectedVehicleID(vehicle._id)}
                disabled={saving}
              >
                <Radio
                  size="small"
                  checked={String(selectedVehicleID) === String(vehicle._id)}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText
                  primary={vehicle.callSign || 'Unnamed vehicle'}
                  primaryTypographyProps={{ fontWeight: 700 }}
                />
                <Stack direction="row" spacing={0.5}>
                  {isCurrent && (
                    <Chip size="small" color="info" label="Current" />
                  )}
                  {vehicle.isBusy && !isCurrent && (
                    <Chip
                      size="small"
                      color="warning"
                      label="In use at this time"
                    />
                  )}
                </Stack>
              </ListItemButton>
            )
          })}
          {!loading && vehicles.length === 0 && (
            <Typography sx={{ px: 2, py: 1.5 }} color="text.secondary">
              No vehicles available for this date.
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {currentVehicleID && (
          <Button
            color="error"
            disabled={saving}
            onClick={() => save(null)}
            sx={{ mr: 'auto' }}
          >
            Remove vehicle
          </Button>
        )}
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            saving ||
            !selectedVehicleID ||
            String(selectedVehicleID) === String(currentVehicleID)
          }
          onClick={() => save(selectedVehicleID)}
        >
          {saving ? 'Saving...' : 'Assign vehicle'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignVehicleToDutyDialog
